import { useState } from 'react';
import { ArrowRightOutlined, BellOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Flex, Input, Typography, theme } from 'antd';
import type { OrigemDeLista, TopicoDeLista } from '@titularis/api-client';
import { toqueAtual } from '@titularis/analytics';
import { registrarEvento } from '../analytics';
import { api } from '../api';
import { destinos } from '../destinos';

/**
 * Um campo de e-mail, e a conta oferecida só depois.
 *
 * **A ordem é a decisão** (D-C, 13/09/2026). A tentação era mandar o link do
 * story para o cadastro e pôr a lista de espera lá dentro; exigir conta antes
 * da inscrição derruba a maior parte delas, e quem entra na fila de um módulo
 * que sai em 2027 não está pronto para criar conta hoje. Aqui o pedido custa um
 * campo, e o convite para a conta aparece na tela de "pronto" - quando a pessoa
 * já disse sim uma vez.
 *
 * Dois estados e nada entre eles: o formulário e a confirmação. Não há
 * "carregando" separado porque o botão já carrega, e não há erro de "esse
 * e-mail já está na lista" porque a API não conta isso a ninguém - reinscrever
 * é a mesma coisa que inscrever, de propósito.
 */
export interface EntrarNaListaProps {
  topic: TopicoDeLista;
  source: OrigemDeLista;
  /** A chamada do botão. Muda com o lugar: "Avise-me", "Quero receber". */
  rotulo?: string;
  /** O que a pessoa lê antes do campo. Ausente, o componente é só o campo. */
  convite?: string;
  /** Em coluna nos cartões estreitos; em linha no rodapé de uma página. */
  vertical?: boolean;
  /** A frase da confirmação, que diz o que foi prometido. */
  confirmacao?: string;
}

export function EntrarNaLista({
  topic,
  source,
  rotulo = 'Avise-me',
  convite,
  vertical = false,
  confirmacao = 'Pronto. Avisaremos assim que sair.',
}: EntrarNaListaProps) {
  const { token } = theme.useToken();
  const [email, setEmail] = useState('');

  const inscricao = useMutation({
    mutationFn: (endereco: string) =>
      api.entrarNaLista({ email: endereco, topic, source, atribuicao: toqueAtual() }),
    /*
     * No sucesso, e não no envio: o que interessa é a inscrição que existe.
     * Contar a tentativa que falhou inflaria a conversão justamente quando
     * alguma coisa está quebrada, que é quando o número precisa ser confiável.
     *
     * O `topic` e o `source` vão junto porque o mesmo formulário aparece em
     * lugares diferentes da página: sem eles, sabe-se que alguém se inscreveu e
     * não onde a página convenceu.
     */
    onSuccess: () => registrarEvento({ nome: 'lead_submitted', topic, origem: source }),
  });

  if (inscricao.isSuccess) {
    return (
      <Flex vertical gap={10} role="status">
        <Flex align="flex-start" gap={8}>
          <CheckCircleFilled style={{ color: token.colorSuccess, marginTop: 3 }} />
          <Typography.Text strong>{confirmacao}</Typography.Text>
        </Flex>
        {/*
          O convite para a conta vem aqui, e só aqui. Quem acabou de pedir para
          ser avisado de um módulo futuro é exatamente quem pode estudar o Enac
          hoje - e agora ele já disse sim uma vez.
        */}
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          Enquanto isso, o módulo Enac já está no ar e a conta gratuita libera questões
          comentadas toda semana.{' '}
          <Typography.Link href={destinos.criarConta}>
            Criar conta grátis <ArrowRightOutlined />
          </Typography.Link>
        </Typography.Text>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={10}>
      {convite ? (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {convite}
        </Typography.Text>
      ) : null}

      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          const endereco = email.trim();
          if (endereco) inscricao.mutate(endereco);
        }}
      >
        <Flex gap={8} vertical={vertical}>
          <Input
            id={`lista-${topic}`}
            type="email"
            required
            inputMode="email"
            autoComplete="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            disabled={inscricao.isPending}
            aria-label="Seu e-mail"
          />
          <Button
            type="primary"
            htmlType="submit"
            loading={inscricao.isPending}
            icon={<BellOutlined />}
            block={vertical}
          >
            {rotulo}
          </Button>
        </Flex>
      </form>

      {inscricao.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Não consegui registrar agora. Tente de novo em instantes."
        />
      ) : null}

      {/*
        A finalidade declarada e a saída, na mesma linha. É a base legal do
        envio (G-CONSENT), e é também o que faz a pessoa se sentir à vontade
        para digitar: ninguém entra numa lista de onde não sabe como sair.
      */}
      <Typography.Text style={{ fontSize: 11, color: token.colorTextTertiary }}>
        Só sobre isto, e você sai quando quiser pelo link de qualquer mensagem.
      </Typography.Text>
    </Flex>
  );
}
